// modalStore.js
import { defineStore } from 'pinia';
import { useProjectGenerationStore } from './projectGenerationStore';

export const useModalStore = defineStore('modals', {
  state: () => ({
    showCardTemplatesPopup: false,
    showNewProjectModal: false,
    showShareModal: false,
    showAddItemModal: false,
    showExportModal: false,
    showProjectSettingsModal: false,
    showFolderSettingsModal: false,
    showConfirmationModal: false,
    /**
     * Create-account modal. Lets a guest convert without being sent to /login,
     * which would open on the keep / sign-in / discard screen and ask them to
     * decide again after they already have.
     */
    showSignUpModal: false,
    /** Which pane it opens on: 'signup' (the usual) or 'login'. */
    signUpModalMode: 'signup',
    showCardCollaboratorsModal: false,
    cardCollaboratorsData: {},
    projectType: 'generic',
    shareData: {
      title: '',
      text: '',
      url: '',
      description: '',
    },
    contentType: 'Content',
    itemType: '',
    parentID: null,
    parent: null,
    projectID: null,
    exportCardsData: [],
    projectSettingsData: null,
    folderSettingsData: null,
    projectDetails: null,

    // Confirmation Modal State
    confirmationModalData: {
      title: 'Are you sure?',
      message: 'This action cannot be undone.',
      note: '',
      confirmButtonLabel: 'Confirm',
      declineButtonLabel: 'Decline',
      cancelButtonLabel: 'Cancel',
      showCloseButton: true,
      onConfirm: null,
      onDecline: null,
      onCancel: null,
    },
    // Toast Notification State
    showToast: false,
    toastMessage: '',
    toastType: 'success', // could be 'success', 'error', 'info'
  }),

  actions: {
    openExportModal(cards = []) {
      // remove cards in recycle bin
      const filteredCards = cards.filter((card) => !card.in_recycle_bin);
      this.exportCardsData = filteredCards;
      this.showExportModal = true;
    },

    closeExportModal() {
      this.showExportModal = false;
      setTimeout(() => {
        this.exportCardsData = [];
      }, 300);
    },

    openCardTemplatesPopup(parent) {
      this.projectType = parent?.project_type || 'generic';
      this.parentID = parent?.id || null;
      this.projectID = parent?.project_id || null;
      this.showCardTemplatesPopup = true;
    },

    closeCardTemplatesPopup() {
      this.showCardTemplatesPopup = false;
      this.parentID = null;
    },

    openNewProjectModal(item) {
      const generationStore = useProjectGenerationStore();

      if (generationStore.isGenerating) {
        this.openConfirmationModal({
          title: 'Project Generation in Progress',
          message:
            'A project is already being generated. Do you want to cancel the current generation to start a new one, or keep it running?',
          confirmButtonLabel: '',
          declineButtonLabel: 'Cancel current generation',
          cancelButtonLabel: 'Keep running',
          showCloseButton: true,
          onDecline: () => {
            generationStore.dismiss();
            this.showNewProjectModal = false;
            setTimeout(() => {
              this.projectType = item?.project_type || 'generic';
              this.parentID = item?.parent_id || null;
              this.parent = item || null;
              this.showNewProjectModal = true;
            }, 50);
          },
          onCancel: () => {
            generationStore.expand();
          },
        });
        return;
      }

      if (generationStore.isActive) {
        generationStore.dismiss();
      }

      this.showNewProjectModal = false;
      setTimeout(() => {
        this.projectType = item?.project_type || 'generic';
        this.parentID = item?.parent_id || null;
        this.parent = item || null;
        this.showNewProjectModal = true;
        generationStore.expand();
      }, 50);
    },

    closeNewProjectModal() {
      this.showNewProjectModal = false;
      this.projectType = 'generic';
      this.parentID = null;
    },

    openShareModal(shareData, contentType) {
      this.showShareModal = true;
      this.shareData = shareData;
      this.contentType = contentType;
    },

    closeShareModal() {
      this.showShareModal = false;
      this.shareData = {
        title: '',
        text: '',
        url: '',
        description: '',
      };
      this.contentType = 'Content';
    },

    openAddItemModal(itemType, parent) {
      this.itemType = itemType;
      this.parent = parent;
      this.parentID = parent?.id || null;
      this.showAddItemModal = true;
    },

    closeAddItemModal() {
      this.showAddItemModal = false;
      this.itemType = '';
      this.parent = null;
      this.parentID = null;
    },
    openCardCollaboratorsModal(cardData) {
      this.cardCollaboratorsData = cardData;
      this.showCardCollaboratorsModal = true;
    },

    closeCardCollaboratorsModal() {
      this.showCardCollaboratorsModal = false;
      this.cardCollaboratorsData = {};
    },

    openProjectSettingsModal(project) {
      this.projectSettingsData = project;
      this.showProjectSettingsModal = true;
    },

    closeProjectSettingsModal() {
      this.showProjectSettingsModal = false;
      setTimeout(() => {
        this.projectSettingsData = null;
      }, 300);
    },

    openFolderSettingsModal(folder) {
      this.folderSettingsData = folder;
      this.showFolderSettingsModal = true;
    },

    closeFolderSettingsModal() {
      this.showFolderSettingsModal = false;
      // Nulled after the leave transition, not with it, or the modal flashes
      // empty on the way out. Same reason as the project one above.
      setTimeout(() => {
        this.folderSettingsData = null;
      }, 300);
    },

    // Confirmation Modal Actions
    openConfirmationModal({
      title = 'Are you sure?',
      message = 'This action cannot be undone.',
      note = '',
      confirmButtonLabel = 'Confirm',
      declineButtonLabel = 'Decline',
      cancelButtonLabel = 'Cancel',
      showCloseButton = true,
      onConfirm = null,
      onDecline = null,
      onCancel = null,
    }) {
      this.confirmationModalData = {
        title,
        message,
        note,
        confirmButtonLabel,
        declineButtonLabel,
        cancelButtonLabel,
        showCloseButton,
        onConfirm,
        onDecline,
        onCancel,
      };
      this.showConfirmationModal = true;
    },

    openSignUpModal(mode = 'signup') {
      this.signUpModalMode = mode === 'login' ? 'login' : 'signup';
      this.showSignUpModal = true;
    },

    closeSignUpModal() {
      this.showSignUpModal = false;
    },

    closeConfirmationModal() {
      this.showConfirmationModal = false;
      setTimeout(() => {
        this.confirmationModalData = {
          title: 'Are you sure?',
          message: 'This action cannot be undone.',
          note: '',
          confirmButtonLabel: 'Confirm',
          declineButtonLabel: 'Decline',
          cancelButtonLabel: 'Cancel',
          showCloseButton: true,
          onConfirm: null,
          onDecline: null,
          onCancel: null,
        };
      }, 300);
    },

    handleConfirmationConfirm() {
      if (this.confirmationModalData.onConfirm) {
        this.confirmationModalData.onConfirm();
      }
      this.closeConfirmationModal();
    },

    handleConfirmationDecline() {
      if (this.confirmationModalData.onDecline) {
        this.confirmationModalData.onDecline();
      }
      this.closeConfirmationModal();
    },

    handleConfirmationCancel() {
      if (this.confirmationModalData.onCancel) {
        this.confirmationModalData.onCancel();
      }
      this.closeConfirmationModal();
    },
    showToastMessage(message, type = 'success', duration = 3000) {
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;

      // auto hide after 3 seconds
      setTimeout(() => {
        this.showToast = false;
      }, duration);
    },

    hideToast() {
      this.showToast = false;
    },
  },
});
